import React, { useState, useEffect } from 'react';
import { Upload, message, Button, Spin } from 'antd';
import { UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { httpPostFormData, customBaseUrl } from '../../store/http';

/**
 * ImageUploadField - A reusable component for uploading images to the backend 
 * and updating the form value with the returned URL.
 * 
 * @param {string} label - The label for the field
 * @param {string} value - The current image URL (from fallback or DB)
 * @param {function} onChange - Callback to update the parent form state
 */
const ImageUploadField = ({ label, value, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value);

  // Sync preview when value changes externally
  useEffect(() => {
    setPreviewUrl(value);
  }, [value]);

  const handleUpload = async (file) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    // We can also append metadata if the backend required it
    // formData.append('type', 'landing_page_image');

    try {
      const response = await httpPostFormData(
        `${customBaseUrl.fileResourseUrl}/upload`, 
        formData
      );

      if (response && response.status) {
        // Assuming backend returns { status: true, data: { url: '...' } }
        const uploadedUrl = response.data?.url || response.data; 
        setPreviewUrl(uploadedUrl);
        onChange(uploadedUrl);
        message.success('Image uploaded successfully');
      } else {
        message.error(response?.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Upload error:', err);
      message.error('An error occurred during upload');
    } finally {
      setLoading(false);
    }
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/svg+xml' || file.type === 'image/webp';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG/SVG/WEBP files!');
      return Upload.LIST_IGNORE;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return Upload.LIST_IGNORE;
    }
    
    // Trigger upload manually
    handleUpload(file);
    return false; // Prevent default antd upload behavior
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#344054' }}>
        {label}
      </label>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: '16px',
        padding: '16px',
        background: '#f9fafb',
        border: '1px solid #eaecf0',
        borderRadius: '12px'
      }}>
        {/* Preview Area */}
        <div style={{ 
          width: '100px', 
          height: '100px', 
          background: '#fff',
          border: '1px solid #eaecf0',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative'
        }}>
          {loading ? (
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          ) : previewUrl ? (
            <img 
              src={previewUrl} 
              alt="Preview" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
            />
          ) : (
            <div style={{ fontSize: '10px', color: '#999', textAlign: 'center', padding: '4px' }}>
               No Image
            </div>
          )}
        </div>

        {/* Action Area */}
        <div style={{ flex: 1 }}>
          <Upload
            name="file"
            showUploadList={false}
            beforeUpload={beforeUpload}
          >
            <Button icon={<UploadOutlined />} loading={loading}>
              Select Image
            </Button>
          </Upload>
          <div style={{ marginTop: '8px' }}>
            <span style={{ fontSize: '12px', color: '#667085' }}>
              Current URL: 
            </span>
            <div style={{ 
              fontSize: '11px', 
              color: 'var(--waya-primary)', 
              wordBreak: 'break-all',
              marginTop: '2px',
              fontFamily: 'monospace'
            }}>
              {previewUrl || 'None'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadField;
